import { z } from "zod";
import {
  notificationChannelSchema,
  notificationStatusSchema,
  notificationTemplateKeySchema,
} from "../enums.ts";
import { isoDateTimeSchema, timestampsSchema, uuidSchema } from "./common.ts";

export const notificationSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema.nullable(),
    userId: uuidSchema,
    channel: notificationChannelSchema,
    templateKey: notificationTemplateKeySchema,
    payload: z.record(z.string(), z.unknown()),
    scheduledFor: isoDateTimeSchema,
    dedupeKey: z.string().nullable(),
    status: notificationStatusSchema,
    providerMessageId: z.string().nullable(),
    error: z.string().nullable(),
    /**
     * When the recipient last opened the feed with this row already in it. Null means unread, which
     * is what the owner's Activity badge counts. Read state lives on the row rather than on the
     * device so an owner who signs in on a laptop and a phone sees one count, not two.
     */
    readAt: isoDateTimeSchema.nullable(),
  })
  .merge(timestampsSchema);
export type Notification = z.infer<typeof notificationSchema>;

export const notificationsResponseSchema = z.object({ items: z.array(notificationSchema) });
export type NotificationsResponse = z.infer<typeof notificationsResponseSchema>;

/**
 * `POST /v1/me/notifications/read`.
 *
 * Marks everything already delivered as read up to and including `through`, rather than taking a
 * list of ids. The client sends the timestamp of the newest row it has actually rendered, so a
 * notification that arrived while the screen was open stays unread instead of being silently
 * cleared by a request that was composed before it existed.
 */
export const markNotificationsReadRequestSchema = z.object({
  through: isoDateTimeSchema,
});
export type MarkNotificationsReadRequest = z.infer<typeof markNotificationsReadRequestSchema>;

export const markNotificationsReadResponseSchema = z.object({ updated: z.number().int() });
export type MarkNotificationsReadResponse = z.infer<typeof markNotificationsReadResponseSchema>;

/** `GET /health` */
export const healthResponseSchema = z.object({
  status: z.literal("ok"),
  service: z.string(),
  version: z.string(),
  timestamp: isoDateTimeSchema,
  database: z.enum(["up", "down", "unchecked"]),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;
